import React, { useCallback, useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import { Button, Row, Col, Menu } from 'antd';

import Layout from '../../Layout';
import { patientTab } from '../../components/Datas';
import { getServiceUrl } from '../../config';

import MedicalRecord from './MedicalRecord';
import Summary from './Summary';
import Symptoms from './Symptoms';
import PatientBasicDetails from './PatientBasicDetails';
import LabResultsTable from './LabResultsTable';
import Genotype from './Genotype';

const apiBaseUrl = getServiceUrl();

const PatientProfile = () => {
  const { patientguid } = useParams();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState({});
  const [labResultsData, setLabResultsData] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const isLastPage = activeTab === patientTab.length;
  const [selectedData, setSelectedData] = useState({});
  const [isSymptomsCompleted, setIsSymptomsCompleted] = useState(false);

  const showAlert = (message, type) => {
    const overlayStyle = {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    };

    const panelStyle = {
      backgroundColor: '#ffffff',
      color: type === 'error' ? '#ff4d4f' : '#5b9bd5',
      borderRadius: '10px',
      padding: '30px',
      width: '90%',
      maxWidth: '400px',
      textAlign: 'center',
      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${type === 'error' ? '#ff4d4f' : '#5b9bd5'}`,
    };

    const buttonStyle = {
      backgroundColor: type === 'error' ? '#ff4d4f' : '#5b9bd5',
      color: '#fff',
      border: 'none',
      borderRadius: '5px',
      padding: '10px 20px',
      cursor: 'pointer',
      fontSize: '14px',
      marginTop: '20px',
      transition: 'background-color 0.3s ease',
    };

    confirmAlert({
      customUI: ({ onClose }) => (
        <div style={overlayStyle}>
          <div style={panelStyle}>
            <p>{message}</p>
            <button style={buttonStyle} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      ),
      closeOnClickOutside: true,
    });
  };

  const getPatientDetails = async (patientguid) => {
    try {
      const config = {
        headers: {
          rdxtoken: localStorage.getItem('tokenId'),
          patientguid,
        },
      };
      const response = await axios.get(
        `${apiBaseUrl}patient/patientmedicalhistory`,
        config,
      );
      return response.data[0];
    } catch (error) {
      console.error(
        'Error fetching patient details:',
        error.response ? error.response.data : error.message,
      );
      return null;
    }
  };

  const refreshPatientData = async () => {
    const data = await getPatientDetails(patientguid);
    if (data) {
      setPatientData(data);
      localStorage.setItem('patientData', JSON.stringify(data));
      if (data.symptomdetails && data.symptomdetails.length >= 3) {
        setIsSymptomsCompleted(true);
      }
    }
  };

  const fetchLabResults = async () => {
    try {
      const config = {
        headers: {
          rdxtoken: localStorage.getItem('tokenId'),
          patientguid,
        },
      };
      const { data } = await axios.get(`${apiBaseUrl}patient/ehr`, config);
      setLabResultsData(data);
    } catch (error) {
      console.error('Error fetching lab results:', error);
      setLabResultsData([]);
    }
  };

  const fetchData = useCallback(async () => {
    if (patientguid) {
      await refreshPatientData();
      await fetchLabResults();
    } else {
      navigate('/patients');
    }
  }, [patientguid, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDataUpdate = (data) => {
    setSelectedData(data);
    if (data.symptomsData && data.symptomsData.length >= 3) {
      setIsSymptomsCompleted(true);
    } else {
      setIsSymptomsCompleted(false);
    }
  };

  const tabPanel = () => {
    switch (activeTab) {
      case 1:
        return (
          <>
            <MedicalRecord
              patientData={patientData}
              patientguid={patientguid}
              onDataUpdate={handleDataUpdate}
            />
            <LabResultsTable
              data={labResultsData}
              onNextTab={() =>
                setActiveTab((prev) => (prev % patientTab.length) + 1)
              }
            />
          </>
        );
      case 2:
        return (
          <Symptoms
            patientData={patientData}
            patientguid={patientguid}
            onDataUpdate={handleDataUpdate}
          />
        );
      case 3:
        return <Summary patientData={patientData} patientguid={patientguid} />;
      default:
        return null;
    }
  };

  const updateSymptomstoDB = async () => {
    try {
      const symptomsDetails = selectedData.symptomsData.map((item) => ({
        source: item.source ?? 'Manual',
        hpoid: item.hpoid,
        sourcefile: null,
        startdate: item.startDate,
        enddate: item.endDate,
      }));
      const formBody = { patientguid, hpoids: symptomsDetails };
      const config = {
        headers: {
          rdxtoken: localStorage.getItem('tokenId'),
        },
      };
      await axios.post(
        apiBaseUrl + 'doctors/overwrite/hpoids',
        formBody,
        config,
      );
    } catch (error) {
      console.error('Error updating symptoms:', error);
    }
  };

  const handleNextClick = async () => {
    await refreshPatientData();
    switch (activeTab) {
      case 1:
        setActiveTab((prev) => (prev % patientTab.length) + 1);
        break;
      case 2:
        if (isSymptomsCompleted) {
          updateSymptomstoDB();
          setActiveTab((prev) => (prev % patientTab.length) + 1);
        } else {
          showAlert(
            'Please select at least 3 symptoms before proceeding.',
            'error',
          );
        }
        break;
      case 3:
        setActiveTab((prev) => (prev % patientTab.length) + 1);
        break;
      default:
        return null;
    }
  };

  const handlePreviousClick = async () => {
    await refreshPatientData();
    setActiveTab((prev) => (prev === 1 ? patientTab.length : prev - 1));
  };

  const handleTabClick = async (tabId) => {
    await refreshPatientData();
    if (tabId === 3 && !isSymptomsCompleted) {
      showAlert(
        'Please select at least 3 symptoms before proceeding.',
        'error',
      );
      return;
    }
    setActiveTab(tabId);
  };

  return (
    <Layout patientguid={patientguid}>
      {patientData && <PatientBasicDetails patientData={patientData} />}

      {/* Horizontal Tabs placed above the main panel */}
      <div className="my-4">
        <Menu
          mode="horizontal" // changed from "inline" to "horizontal"
          selectedKeys={[`${activeTab}`]}
          onClick={({ key }) => handleTabClick(Number(key))}
          style={{
            borderBottom: 'none', // remove default border for a chrome-like feel
          }}
        >
          {patientTab.map((tab) => (
            <Menu.Item key={tab.id} icon={<tab.icon />}>
              {tab.title}
            </Menu.Item>
          ))}
        </Menu>
      </div>

      <div className="my-8 rounded-xl border border-border bg-white p-6">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Button
              onClick={handlePreviousClick}
              disabled={activeTab === 1}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Previous
            </Button>
          </Col>
          <Col>
            {isLastPage ? (
              <Button
                type="primary"
                onClick={() =>
                  navigate(`/patients/diseases/${patientData.patientguid}`, {
                    patientData: { patientData },
                  })
                }
              >
                Get Differential Diagnosis
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleNextClick}
                icon={<FontAwesomeIcon icon={faArrowRight} />}
              >
                Next
              </Button>
            )}
          </Col>
        </Row>

        {/* Tab Content */}
        {tabPanel()}
      </div>
    </Layout>
  );
};

export default PatientProfile;
